import React, { useState, useEffect, useCallback } from "react";
import LoginApi from "../api/Login";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./../vendor/css/login.css";

const Login = () => {
  const [cookies, setCookie, removeCookie] = useCookies([
    "token",
    "user",
    "admin",
  ]);
  const navigate = useNavigate();

  useEffect(() => {
    if (cookies.token) {
      navigate("/");
    }
  }, [cookies.token, navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Real-time email validation
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // Password strength checker
  const checkPasswordStrength = useCallback((password) => {
    if (password.length === 0) return null;
    if (password.length < 6) return 'weak';
    if (password.length < 8) return 'medium';
    if (password.length >= 8 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) return 'strong';
    return 'medium';
  }, []);

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value.length > 0) {
      setEmailValid(validateEmail(value));
    } else {
      setEmailValid(null);
    }
  };

  // Handle password change with strength check
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordStrength(checkPasswordStrength(value));
  };

  // Security: Rate limiting for login attempts
  const handleLoginAttempt = useCallback(() => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    
    if (newAttempts >= 5) {
      setIsBlocked(true);
      setBlockTimeRemaining(300); // 5 minutes
      
      const timer = setInterval(() => {
        setBlockTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsBlocked(false);
            setLoginAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [loginAttempts]);

  const validateForm = () => {
    if (!email.trim()) {
      setError("Email address is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!password.trim()) {
      setError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isBlocked) {
      setError(`Too many failed attempts. Please wait ${Math.ceil(blockTimeRemaining / 60)} minutes.`);
      return;
    }

    if (!validateForm()) return;
    
    setError("");
    setIsLoading(true);

    try {
      const response = await LoginApi({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (response.status === 200) {
        // Reset login attempts on successful login
        setLoginAttempts(0);
        setShowSuccessModal(true);

        setTimeout(() => {
          const cookieOptions = rememberMe ? { expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) } : {};
          
          setCookie("token", response?.token, cookieOptions);
          setCookie("user", response?.user?.name, cookieOptions);
          setCookie("admin", true, cookieOptions);
          setCookie("id", response?.user?.id || null, cookieOptions);

          setShowSuccessModal(false);
          Swal.fire({
            title: "Login Successful!",
            text: "Welcome to AACore Holiday Admin Portal!",
            icon: "success",
            draggable: true,
            timer: 1500,
            showConfirmButton: false,
          }).then(() => {
            navigate("/");
          });
        }, 1500);
      }
    } catch (err) {
      console.error("Login error:", err);
      handleLoginAttempt();
      
      if (err.code === "ERR_NETWORK") {
        setError(
          "Unable to connect to server. Please check your internet connection or contact support.",
        );
      } else if (err.response?.status === 401) {
        setError("Invalid email or password. Please try again.");
      } else if (err.response?.status === 429) {
        setError("Too many login attempts. Please try again later.");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <section className="login-section" role="main" aria-labelledby="login-title">
      {/* Mesh Gradient Background */}
      <div className="mesh-gradient"></div>

      {/* Floating Orbs */}
      <div className="floating-orb orb-1"></div>
      <div className="floating-orb orb-2"></div>
      <div className="floating-orb orb-3"></div>

      {/* Particles Container */}
      <div className="particles-container" id="particles"></div>

      {/* Background Shapes */}
      <div className="login-bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      {/* Login Container */}
      <div className="login-container">
        <div className="login-card">
          {/* Logo & Header */}
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/assets/images/AacoreHoliday-logo.png"
                alt="AACore Holiday"
                className="logo-img"
              />
            </div>
            <h2 id="login-title">Welcome Back</h2>
            <p>Sign in to your admin account</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Email Input */}
            <div className="input-group">
              <div className={`input-wrapper ${emailValid === true ? 'valid' : emailValid === false ? 'invalid' : ''}`}>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder=" "
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  required
                  aria-describedby={error ? "error-message" : undefined}
                />
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <div className="input-icon">
                  {emailValid === true ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : emailValid === false ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            {/* Password Input */}
            <div className="input-group">
              <div className={`input-wrapper ${passwordStrength ? passwordStrength : ''}`}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="form-input"
                  placeholder=" "
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  minLength="6"
                  required
                  aria-describedby={error ? "error-message" : undefined}
                />
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePassword}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex="0"
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
                {passwordStrength && (
                  <div className="input-strength"></div>
                )}
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">Remember me for 30 days</span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-container" role="alert" aria-live="polite">
                <svg
                  className="error-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8v4m0 4h.01"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="error-message" id="error-message">{error}</span>
              </div>
            )}

            {/* Login Attempts Warning */}
            {loginAttempts > 2 && !isBlocked && (
              <div className="warning-container" role="alert" aria-live="polite">
                <svg
                  className="warning-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span className="warning-message">
                  Warning: {5 - loginAttempts} attempts remaining before temporary lockout
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isLoading || isBlocked}
              aria-describedby={isBlocked ? "block-message" : undefined}
            >
              <span className="btn-text">
                {isBlocked 
                  ? `Blocked (${Math.ceil(blockTimeRemaining / 60)}m)` 
                  : isLoading 
                    ? "Signing in..." 
                    : "Sign In to Admin Portal"
                }
              </span>
              {!isBlocked && (
                <div className="btn-arrow">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
              {isLoading && (
                <div className="loading-spinner" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="spinner-bg"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      opacity="0.25"
                    />
                    <path
                      className="spinner-path"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p>Protected by enterprise-grade security</p>
            <div className="security-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>256-bit SSL encryption</span>
            </div>
          </div>
        </div>

        {/* Brand Footer */}
        <p className="brand-footer">AACore Holiday Admin Portal</p>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon-container">
              <svg
                className="success-icon"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M5 13l4 4L19 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3>Login Successful!</h3>
            <p>You have successfully logged in!</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Login;
